import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft,
  Users,
  Clock,
  CheckSquare,
  Calendar,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  StatusBadge,
  Badge,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  PriorityBadge,
} from '@/components/ui';
import { ProjectFormModal, TaskFormModal } from '@/components/forms';
import { useProject, useProjectTasks, useProjectTeam } from '@/hooks/useProjects';
import { formatDate, formatDuration, formatCurrency } from '@aelita/shared';
import type { Project, Task } from '@aelita/shared';

export const Route = createFileRoute('/project/$projectId')({
  component: ProjectDashboard,
});

function ProjectDashboard() {
  const { projectId } = Route.useParams();
  const { data: project, isLoading } = useProject(projectId);
  const { data: tasks } = useProjectTasks(projectId);
  const { data: team } = useProjectTeam(projectId);

  // Modal states
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">Loading project details...</p>
        </PageContainer>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <Header title="Project Not Found" />
        <PageContainer>
          <p className="text-gray-500 dark:text-gray-400">The project you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/projects" className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block">
            Back to Projects
          </Link>
        </PageContainer>
      </>
    );
  }

  const projectAny = project as any;
  const taskStats = projectAny.stats?.tasks ?? {};
  const totalHours = projectAny.stats?.totalHoursLogged ?? 0;
  const taskList = (tasks as any[]) ?? [];
  const teamList = (team as any[]) ?? [];

  // Calculate derived stats
  const totalTasks = Object.values(taskStats).reduce((a: number, b: any) => a + (b as number), 0);
  const _completedTasks = (taskStats.done ?? 0) + (taskStats.cancelled ?? 0);
  const inProgressTasks = taskStats.in_progress ?? 0;
  const todoTasks = (taskStats.backlog ?? 0) + (taskStats.todo ?? 0);
  const completionRate =
    totalTasks > 0 ? Math.round(((taskStats.done ?? 0) / totalTasks) * 100) : 0;

  // Budget calculations
  const budgetHours = Number(projectAny.budgetHours) || 0;
  const budgetAmount = Number(projectAny.budgetAmount) || 0;
  const hourlyRate = Number(projectAny.hourlyRate) || 0;
  const budgetUsedPercent = budgetHours > 0 ? Math.min(100, (totalHours / budgetHours) * 100) : 0;
  const estimatedCost = totalHours * hourlyRate;
  const budgetRemaining = budgetAmount > 0 ? budgetAmount - estimatedCost : 0;

  // Timeline calculations
  const startDate = projectAny.startDate ? new Date(projectAny.startDate) : null;
  const targetEndDate = projectAny.targetEndDate ? new Date(projectAny.targetEndDate) : null;
  const now = new Date();
  const isOverdue = targetEndDate && targetEndDate < now && projectAny.status !== 'completed';
  const daysRemaining = targetEndDate
    ? Math.ceil((targetEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Overdue tasks
  const overdueTasks = taskList.filter(
    (t: any) =>
      t.dueDate && new Date(t.dueDate) < now && t.status !== 'done' && t.status !== 'cancelled',
  );

  return (
    <>
      <Header
        title={projectAny.name}
        subtitle={
          <div className="flex items-center gap-2">
            <span>{projectAny.client?.name ?? 'Internal Project'}</span>
            <StatusBadge status={projectAny.status} />
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to="/projects">
              <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            <Button onClick={() => setIsEditProjectOpen(true)}>Edit Project</Button>
          </div>
        }
      />
      <PageContainer>
        {/* Alert for overdue */}
        {isOverdue && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <div>
              <p className="font-medium text-red-800 dark:text-red-300">Project is overdue</p>
              <p className="text-sm text-red-600 dark:text-red-400">
                Target end date was {formatDate(targetEndDate!)}
              </p>
            </div>
          </div>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-950 rounded-lg">
                <CheckSquare className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalTasks}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Tasks</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-950 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completionRate}%</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Complete</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-950 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalHours.toFixed(1)}h</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Hours Logged</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teamList.length}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Team Members</p>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-950 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {daysRemaining !== null ? (daysRemaining > 0 ? daysRemaining : 0) : '-'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Days Left</p>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Budget & Progress */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Budget & Progress</h2>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-2 gap-6">
                  {/* Hours Budget */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Hours Budget</span>
                      <span className="text-sm font-medium">
                        {budgetHours > 0
                          ? `${totalHours.toFixed(0)} / ${budgetHours}h`
                          : 'No budget set'}
                      </span>
                    </div>
                    {budgetHours > 0 && (
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${
                            budgetUsedPercent > 90
                              ? 'bg-red-500'
                              : budgetUsedPercent > 70
                                ? 'bg-yellow-500'
                                : 'bg-primary-500'
                          }`}
                          style={{ width: `${budgetUsedPercent}%` }}
                        />
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {budgetHours > 0 ? `${budgetUsedPercent.toFixed(0)}% used` : ''}
                    </p>
                  </div>
                  {/* Task Progress */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Task Progress</span>
                      <span className="text-sm font-medium">
                        {taskStats.done ?? 0} / {totalTasks} done
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div
                        className="bg-green-500 h-3 rounded-full"
                        style={{ width: `${completionRate}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {inProgressTasks} in progress, {todoTasks} to do
                    </p>
                  </div>
                </div>

                {/* Financial Summary */}
                {(budgetAmount > 0 || hourlyRate > 0) && (
                  <div className="mt-6 pt-4 border-t grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Estimated Cost</p>
                      <p className="text-lg font-semibold">{formatCurrency(estimatedCost)}</p>
                    </div>
                    {budgetAmount > 0 && (
                      <>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Total Budget</p>
                          <p className="text-lg font-semibold">{formatCurrency(budgetAmount)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Remaining</p>
                          <p
                            className={`text-lg font-semibold ${budgetRemaining < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
                          >
                            {formatCurrency(budgetRemaining)}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Project Details */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Project Details</h2>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-3 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Type</dt>
                    <dd className="mt-1">
                      <Badge variant="gray">{projectAny.type}</Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Billing Type</dt>
                    <dd className="mt-1 font-medium capitalize">{projectAny.billingType}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Hourly Rate</dt>
                    <dd className="mt-1 font-medium">
                      {hourlyRate > 0 ? `${formatCurrency(hourlyRate)}/hr` : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Start Date</dt>
                    <dd className="mt-1 font-medium">{startDate ? formatDate(startDate) : '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Target End Date</dt>
                    <dd className={`mt-1 font-medium ${isOverdue ? 'text-red-600 dark:text-red-400' : ''}`}>
                      {targetEndDate ? formatDate(targetEndDate) : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Client</dt>
                    <dd className="mt-1 font-medium">
                      {projectAny.client ? (
                        <Link
                          to="/client/$clientId"
                          params={{ clientId: projectAny.client.id }}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {projectAny.client.name}
                        </Link>
                      ) : (
                        'Internal'
                      )}
                    </dd>
                  </div>
                </dl>
                {projectAny.description && (
                  <div className="mt-4 pt-4 border-t">
                    <dt className="text-sm text-gray-500 dark:text-gray-400 mb-1">Description</dt>
                    <dd className="text-gray-700 dark:text-gray-200">{projectAny.description}</dd>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Overdue Tasks Alert */}
            {overdueTasks.length > 0 && (
              <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
                <CardHeader>
                  <h2 className="font-semibold text-red-800 dark:text-red-300">
                    Overdue Tasks ({overdueTasks.length})
                  </h2>
                </CardHeader>
                <CardBody className="space-y-2">
                  {overdueTasks.slice(0, 5).map((task: any) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded border border-red-100 dark:border-red-800"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          Due {formatDate(new Date(task.dueDate))}
                        </p>
                      </div>
                      <PriorityBadge priority={task.priority} />
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}

            {/* Tasks */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Tasks</h2>
                <Button size="sm" onClick={() => setIsAddTaskOpen(true)}>
                  Add Task
                </Button>
              </CardHeader>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Task</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Priority</TableHeader>
                    <TableHeader>Time Logged</TableHeader>
                    <TableHeader>Due Date</TableHeader>
                    <TableHeader>Assignees</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskList.length > 0 ? (
                    taskList.slice(0, 10).map((task: any) => (
                      <TableRow key={task.id}>
                        <TableCell>
                          <button
                            onClick={() => setEditingTask(task)}
                            className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 text-left"
                          >
                            {task.title}
                          </button>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={task.status} />
                        </TableCell>
                        <TableCell>
                          <PriorityBadge priority={task.priority} />
                        </TableCell>
                        <TableCell>
                          <span
                            className={task.timeLoggedMinutes > 0 ? 'font-medium' : 'text-gray-400'}
                          >
                            {task.timeLoggedMinutes > 0
                              ? formatDuration(task.timeLoggedMinutes)
                              : '-'}
                          </span>
                          {task.estimatedHours && (
                            <span className="text-gray-400 text-sm ml-1">
                              / {task.estimatedHours}h
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {task.dueDate ? (
                            <span
                              className={
                                new Date(task.dueDate) < now && task.status !== 'done'
                                  ? 'text-red-600 dark:text-red-400'
                                  : ''
                              }
                            >
                              {formatDate(new Date(task.dueDate))}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex -space-x-2">
                            {task.assignments?.slice(0, 3).map((a: any) => (
                              <Avatar
                                key={a.employee.id}
                                src={a.employee.avatarUrl}
                                name={`${a.employee.firstName} ${a.employee.lastName}`}
                                size="sm"
                                className="border-2 border-white dark:border-gray-900"
                              />
                            ))}
                            {task.assignments?.length > 3 && (
                              <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium border-2 border-white dark:border-gray-900">
                                +{task.assignments.length - 3}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500 dark:text-gray-400">
                        No tasks yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {taskList.length > 10 && (
                <div className="p-3 border-t text-center">
                  <Link
                    to="/tasks"
                    search={{ projectId }}
                    className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
                  >
                    View all {taskList.length} tasks
                  </Link>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Task Distribution */}
            <Card>
              <CardHeader>
                <h2 className="font-semibold">Task Distribution</h2>
              </CardHeader>
              <CardBody className="space-y-3">
                {[
                  { label: 'Done', value: taskStats.done ?? 0, color: 'bg-green-500' },
                  { label: 'In Progress', value: taskStats.in_progress ?? 0, color: 'bg-blue-500' },
                  { label: 'In Review', value: taskStats.review ?? 0, color: 'bg-purple-500' },
                  { label: 'To Do', value: taskStats.todo ?? 0, color: 'bg-yellow-500' },
                  { label: 'Backlog', value: taskStats.backlog ?? 0, color: 'bg-gray-400' },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-300">{item.label}</span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`${item.color} h-2 rounded-full`}
                        style={{
                          width: totalTasks > 0 ? `${(item.value / totalTasks) * 100}%` : '0%',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>

            {/* Team */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Team ({teamList.length})</h2>
                <Button size="sm" variant="ghost">
                  Manage
                </Button>
              </CardHeader>
              <CardBody className="space-y-3">
                {teamList.length > 0 ? (
                  teamList.map((assignment: any) => (
                    <div key={assignment.id} className="flex items-center gap-3">
                      <Avatar
                        src={assignment.employee.avatarUrl}
                        name={`${assignment.employee.firstName} ${assignment.employee.lastName}`}
                      />
                      <div className="flex-1 min-w-0">
                        <Link
                          to="/employee/$employeeId"
                          params={{ employeeId: assignment.employee.id }}
                          className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 truncate block"
                        >
                          {assignment.employee.firstName} {assignment.employee.lastName}
                        </Link>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {assignment.role ?? assignment.employee.title}
                        </p>
                      </div>
                      {assignment.allocatedHoursPerWeek && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {assignment.allocatedHoursPerWeek}h/wk
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No team members assigned</p>
                )}
              </CardBody>
            </Card>

            {/* Milestones */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h2 className="font-semibold">Milestones</h2>
                <Button size="sm" variant="ghost">
                  Add
                </Button>
              </CardHeader>
              <CardBody className="space-y-3">
                {projectAny.milestones?.length > 0 ? (
                  projectAny.milestones.map((milestone: any) => (
                    <div key={milestone.id} className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 h-3 w-3 rounded-full flex-shrink-0 ${
                          milestone.status === 'completed'
                            ? 'bg-green-500'
                            : milestone.status === 'in_progress'
                              ? 'bg-yellow-500'
                              : milestone.status === 'missed'
                                ? 'bg-red-500'
                                : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{milestone.name}</p>
                        {milestone.dueDate && (
                          <p
                            className={`text-sm ${
                              new Date(milestone.dueDate) < now && milestone.status !== 'completed'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            Due {formatDate(new Date(milestone.dueDate))}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No milestones</p>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </PageContainer>

      {/* Edit Project Modal */}
      <ProjectFormModal
        isOpen={isEditProjectOpen}
        onClose={() => setIsEditProjectOpen(false)}
        project={project as Project}
      />

      {/* Add Task Modal */}
      <TaskFormModal
        isOpen={isAddTaskOpen}
        onClose={() => setIsAddTaskOpen(false)}
        task={null}
        defaultProjectId={projectId}
      />

      {/* Edit Task Modal */}
      <TaskFormModal
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        task={editingTask}
        defaultProjectId={projectId}
      />
    </>
  );
}
